import {Component, computed, Input, OnChanges, OnDestroy, signal, SimpleChanges, TemplateRef} from '@angular/core';
import {
    AbstractControl,
    ControlValueAccessor,
    FormBuilder, NG_VALIDATORS, NG_VALUE_ACCESSOR, ReactiveFormsModule,
    UntypedFormGroup,
    ValidationErrors,
    Validators
} from "@angular/forms";
import {map, startWith, switchMap, take} from "rxjs/operators";
import {
    arrayIsSet,
    getOnlyValuesEnumNumber, stringIsSet,
} from "../../providers/service/global-variables.service";
import {combineLatest, Observable, of, pipe, Subscription} from "rxjs";
import {ElementParse_v2} from "../../ParseClass/Element.Parse";
import {MatDialog} from "@angular/material/dialog";
import {ExternalInfoParse, ExternalInfoType} from "../../ParseClass/IoTDeviceConfiguration.Parse";
import {SensorDataTypesParse} from "../../ParseClass/sensor-data-types-parse";
import {dataForm} from "../auto-complete-form/auto-complete-form.component";
import {PossibleValuesFormField} from "../multi-select-form-field/multi-select-form-field.component";
import {TranslateService} from "@ngx-translate/core";

@Component({
    selector: 'app-form-field-external-info',
    standalone: false,
    templateUrl: './form-field-external-info.component.html',
    styleUrl: './form-field-external-info.component.scss',
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            multi: true,
            useExisting: FormFieldExternalInfoComponent
        },
        {
            provide: NG_VALIDATORS,
            multi: true,
            useExisting: FormFieldExternalInfoComponent
        }
    ]
})
export class FormFieldExternalInfoComponent implements OnChanges, OnDestroy, ControlValueAccessor {
    @Input() title: string = ''
    elementsSignal = signal<ElementParse_v2[] | undefined>(undefined);
    elementsPossibleValues = computed(() => {
        const elements = this.elementsSignal()
        if (arrayIsSet(elements)) {
            return elements.map(el => {
                return {
                    html: el.name,
                    valueForm: el.objectId
                } as dataForm
            })
        } else {
            return []
        }
    });
    elementsPossibleValues_v2 = computed(() => {
        const elements = this.elementsSignal()
        if (arrayIsSet(elements)) {
            return elements.map(el => {
                return {
                    noTraduction: el.name,
                    objectId: el.objectId,
                    registerValue: el.objectId
                } as PossibleValuesFormField
            })
        } else {
            return []
        }
    });

    allSensorDataIdUsed$: Observable<string[] | undefined>
    allInfoNamesUsed$: Observable<string[] | undefined>
    warningMessages$: Observable<{ message: string, infoName?: string } | undefined>

    @Input() set elements(value: ElementParse_v2[] | undefined) {
        this.elementsSignal.set(value);
    }

    get elements() {
        return this.elementsSignal()
    }

    sensorsDataSignal = signal<SensorDataTypesParse[] | undefined>(undefined);
    sensorsDataPossibleValues = computed(() => {
        const elements = this.sensorsDataSignal()
        if (arrayIsSet(elements)) {
            return elements.map(el => {
                return {
                    html: el.getTraductionByLang(this.ts.currentLang),
                    valueForm: el.name
                } as dataForm
            })
        } else {
            return []
        }
    });

    @Input() set sensorsData(value: SensorDataTypesParse[] | undefined) {
        this.sensorsDataSignal.set(value);
    }

    get sensorsData() {
        return this.sensorsDataSignal()
    }

    form: UntypedFormGroup;
    subscriptions = new Subscription();
    editValue$: Observable<any | undefined>
    editExtenalInfo$: Observable<ExternalInfoParse | undefined>
    values$: Observable<ExternalInfoParse[] | undefined>
    externalInfoGrouped$: Observable<{ type: ExternalInfoType, values: ExternalInfoParse[] }[] | undefined>
    externalInfoType = ExternalInfoType
    numberKeyValues$: Observable<number>
    editType$: Observable<ExternalInfoType>
    isEdit = signal<{ index: number } | undefined>(undefined)

    get editType() {
        return this.form.get('editType')!.value
    }

    constructor(
        private ts: TranslateService,
        private fb: FormBuilder,
        private matDialog: MatDialog) {
        this.form = fb.group({
            editValue: fb.group({
                sensorId: undefined,
                infoName: undefined,
                sensorIds: undefined,
                sensorIdsDiff: undefined,
                sensorIdsSum: undefined,
                minutes: undefined,
            }),
            editType: undefined,
            recordSetpoint: undefined,
            recordWeatherTemperature: undefined,
            value: [undefined, [Validators.required]]
        })
        this.editValue$ = this.form.get('editValue')!.valueChanges.pipe(
            startWith(undefined),
            map(values => {
                return this.form.get('editValue')!.value
            })
        )
        this.editExtenalInfo$ = this.editValue$.pipe(
            map(values => {
                return this.getExternalInfoByEditValue()
            })
        )
        this.values$ = this.form.get('value')!.valueChanges.pipe(
            startWith(undefined),
            map(values => {
                if (arrayIsSet(this.form.get('value')!.value)) {
                    return this.form.get('value')!.value as ExternalInfoParse[]
                } else {
                    return undefined
                }
            })
        )
        this.numberKeyValues$ = this.values$.pipe(map(v => arrayIsSet(v) ? v.length : 0))
        this.allSensorDataIdUsed$ = this.values$.pipe(
            map((v) => {
                if (arrayIsSet(v)) {
                    let sensorIds: string[] = [];
                    v.forEach((external: any) => {
                        if (arrayIsSet(external.allSensorIds)) {
                            sensorIds = sensorIds.concat(external.allSensorIds)
                        }
                    })
                    return sensorIds
                } else {
                    return undefined
                }
            })
        );
        this.allInfoNamesUsed$ = this.values$.pipe(
            map((v) => {
                if (arrayIsSet(v)) {
                    let infoNames: string[] = [];
                    v.forEach((external: any) => {
                        if (stringIsSet(external.currentInfoName)) {
                            infoNames.push(external.currentInfoName)
                        }
                    })
                    return infoNames
                } else {
                    return undefined
                }
            })
        );
        this.warningMessages$ = combineLatest([this.editExtenalInfo$, this.values$]).pipe(
            map(([editValue, currentValue]) => {
                if (currentValue && currentValue.some((c, index) => (this.isEdit()==null || this.isEdit()?.index != index) && c.currentInfoName == editValue?.currentInfoName!)) {
                    // const replySensorData=currentValue.find(c => c.currentInfoName == editValue?.currentInfoName!)
                    return {message: 'IotDevices.replySensorData', infoName: editValue!.currentInfoName}
                } else {
                    return
                }
            })
        )
        this.editType$ = this.form.get('editType')!.valueChanges.pipe(
            startWith(undefined),
            map(() => this.form.get('editType')?.value)
        )
        this.externalInfoGrouped$ = this.values$.pipe(
            map(values => {
                const map = new Map<ExternalInfoType, ExternalInfoParse[]>();
                getOnlyValuesEnumNumber(this.externalInfoType).forEach(type => {
                    map.set(parseInt(type), []);
                })
                if (arrayIsSet(values)) {
                    values.forEach((info, index) => {
                        const type = info.type; // usa il getter definito nella classe
                        if (!map.has(type)) {
                            map.set(type, []);
                        }
                        info.positionIndex = index
                        map.get(type)!.push(info);
                    })
                }
                return Array.from(map.entries()).map(([type, values]) => ({type, values}));

            })
        )
    }

    ngOnChanges(changes: SimpleChanges) {
    }

    ngOnDestroy() {
    }

    onTouched: Function = () => {
    };

    registerOnChange(onChange: any) {

        this.subscriptions.add(
            this.form!.get('value')!.valueChanges
                .subscribe(onChange)
        );

    }

    setDisabledState(disabled: boolean) {
        if (disabled) {
            this.form.disable();
        } else {
            this.form.enable();
        }
    }

    writeValue(value: ExternalInfoParse[]) {
        if (arrayIsSet(value)) {
            setTimeout(() => {
                const recordSetpoint = value.find(v => v.type == this.externalInfoType.RECORD_SETPOINT)
                if (recordSetpoint) {
                    this.form.get('recordSetpoint')?.setValue(recordSetpoint.recordSetpoint == 1)
                }
                const recordWeatherTemperature = value.find(v => v.type == this.externalInfoType.RECORD_WEATHER_TEMPERATURE)

                if (recordWeatherTemperature) {
                    this.form.get('recordWeatherTemperature')?.setValue(recordWeatherTemperature.recordWeatherTemperature == 1)
                }
                this.form.get('value')?.setValue(value)
            })
        } else {
            this.form.reset();
        }
    }

    validate(control: AbstractControl): ValidationErrors | null {
        // if (!isNotNullOrUndefined(this.form.get('autoComplete')?.validator)) {
        //     this.form.get('autoComplete')?.setValidators(control.validator)
        //     this.form.get('autoComplete')?.updateValueAndValidity()
        // }
        if (this.form.valid) {
            return null
        } else {
            return this.form.errors!;
        }
    }

    addControlErrors(allErrors: any, controlName: string) {
        const errors = {...allErrors};
        const controlErrors = this.form.controls[controlName].errors;
        if (controlErrors) {
            errors[controlName] = controlErrors;
        }
        return errors;
    }

    registerOnTouched(onTouched: Function) {
        this.onTouched = onTouched;
    }

    getElementNamePredicate(elementId: string, elements: ElementParse_v2[] | undefined) {
        const element = elements?.find(el => el.objectId == elementId)
        return element ? element.name : elementId
    }

    getSensorDataNamePredicate(sensorId: string, sensordata: SensorDataTypesParse[] | undefined) {
        const sensor = sensordata?.find(el => el.name == sensorId)
        return sensor ? sensor.name : sensorId
    }

    getMultipleAndSinglePredicate(value: {
        infoName: string,
        sensorIds: string[]
    }, elements: ElementParse_v2[] | undefined, sensordata: SensorDataTypesParse[] | undefined) {
        const sensor = sensordata?.find(el => el.name == value.infoName)
        const names = value.sensorIds?.reduce((prev, curr) => {
            const element = elements?.find(el => el.objectId == curr)
            if (element) {
                prev.push(element.name)
            } else {
                prev.push(curr)
            }
            return prev
        }, [] as string[])
        return {names, infoName: sensor ? sensor.name : value.infoName}
    }

    getDifSumAndSinglePredicate(value: ExternalInfoParse, elements: ElementParse_v2[] | undefined, sensordata: SensorDataTypesParse[] | undefined) {
        const sensor = sensordata?.find(el => el.name == value.diff?.infoName)
        const elementsNameDiff = value.diff?.sensorIdsDiff.reduce((prev, curr) => {
            const element = elements?.find(el => el.objectId == curr)
            if (element) {
                prev.push(element.name)
            } else {
                prev.push(curr)
            }
            return prev
        }, [] as string[])
        const elementsNameSum = value.diff?.sensorIdsSum.reduce((prev, curr) => {
            const element = elements?.find(el => el.objectId == curr)
            if (element) {
                prev.push(element.name)
            } else {
                prev.push(curr)
            }
            return prev
        }, [] as string[])
        return {elementsNameSum, elementsNameDiff, infoName: sensor ? sensor.name : value.infoName}
    }

    addExternalInfo(dialogComponent: TemplateRef<any>) {
        const dialogref = this.matDialog.open(dialogComponent, {
            maxWidth: '100vw', autoFocus: false,
            exitAnimationDuration: 0.1
        })
        dialogref.afterClosed().subscribe(() => {
        });
    }

    unsetIsEdit() {
        this.isEdit.set(undefined)
    }

    addNew(type: ExternalInfoType, dialogComponent: TemplateRef<any>) {
        this.form.get('editType')?.setValue(type)
        const dialogref = this.matDialog.open(dialogComponent, {
            maxWidth: '100vw', autoFocus: false,
            exitAnimationDuration: 0.1
        })
        dialogref.afterClosed().subscribe(() => {
        });
    }

    addValueInExternalInfo(value: ExternalInfoParse) {
        const values = arrayIsSet(this.form.get('value')?.value) ? [...this.form.get('value')?.value] : [];
        if (value.type == ExternalInfoType.RECORD_SETPOINT) {
            const index = values.findIndex(v => v.type == ExternalInfoType.RECORD_SETPOINT)
            if (index >= 0) {
                values.splice(index, 1)
            }
        } else if (value.type == ExternalInfoType.RECORD_WEATHER_TEMPERATURE) {
            const index = values.findIndex(v => v.type == ExternalInfoType.RECORD_WEATHER_TEMPERATURE)
            if (index >= 0) {
                values.splice(index, 1)
            }
        }
        values.push(value)
        this.form.get('value')?.setValue(values)
    }

    getExternalInfoByEditValue() {
        const externalInfo = new ExternalInfoParse()
        if (this.editType == this.externalInfoType.FROM) {
            externalInfo.sensorId = this.form.get('editValue')?.get('sensorId')?.value
            externalInfo.infoName = this.form.get('editValue')?.get('infoName')?.value
        } else if (this.editType == this.externalInfoType.RECORD_SETPOINT) {
            externalInfo.recordSetpoint = this.form.get('editValue')?.get('recordSetpoint')?.value
        } else if (this.editType == this.externalInfoType.MAX) {
            externalInfo.max = {
                sensorIds: this.form.get('editValue')?.get('sensorIds')?.value,
                infoName: this.form.get('editValue')?.get('infoName')?.value
            }
        } else if (this.editType == this.externalInfoType.MEAN) {
            externalInfo.mean = {
                sensorIds: this.form.get('editValue')?.get('sensorIds')?.value,
                infoName: this.form.get('editValue')?.get('infoName')?.value
            }
        } else if (this.editType == this.externalInfoType.MIN) {
            externalInfo.min = {
                sensorIds: this.form.get('editValue')?.get('sensorIds')?.value,
                infoName: this.form.get('editValue')?.get('infoName')?.value
            }
        } else if (this.editType == this.externalInfoType.SUM) {
            externalInfo.sum = {
                sensorIds: this.form.get('editValue')?.get('sensorIds')?.value,
                infoName: this.form.get('editValue')?.get('infoName')?.value
            }
        } else if (this.editType == this.externalInfoType.DIFF) {
            externalInfo.diff = {
                sensorIdsSum: this.form.get('editValue')?.get('sensorIdsSum')?.value,
                sensorIdsDiff: this.form.get('editValue')?.get('sensorIdsDiff')?.value,
                infoName: this.form.get('editValue')?.get('infoName')?.value,
            }
        } else if (this.editType == this.externalInfoType.MOVING_MEAN) {
            externalInfo.movingMean = {
                sensorId: this.form.get('editValue')?.get('sensorId')?.value,
                infoName: this.form.get('editValue')?.get('infoName')?.value,
                minutes: this.form.get('editValue')?.get('minutes')?.value,
            }
        } else if (this.editType == this.externalInfoType.VARIATION) {
            externalInfo.variation = {
                sensorId: this.form.get('editValue')?.get('sensorId')?.value,
                infoName: this.form.get('editValue')?.get('infoName')?.value,
            }
        } else if (this.editType == this.externalInfoType.TIME_DERIVATE) {
            externalInfo.timeDerivate = {
                sensorId: this.form.get('editValue')?.get('sensorId')?.value,
                infoName: this.form.get('editValue')?.get('infoName')?.value,
            }
        }
        return externalInfo
    }

    addInValue() {
        this.addValueInExternalInfo(this.getExternalInfoByEditValue())
    }

    addRecordSetpoint(checked: boolean) {
        const externalInfo = new ExternalInfoParse()
        externalInfo.recordSetpoint = checked ? 1 : 0
        this.addValueInExternalInfo(externalInfo);
    }

    addRecordWeatherTemperature(checked: boolean) {
        const externalInfo = new ExternalInfoParse()
        externalInfo.recordWeatherTemperature = checked ? 1 : 0
        this.addValueInExternalInfo(externalInfo);
    }

    editExternalInfo(externalInfo: ExternalInfoParse, template: TemplateRef<any>) {
        this.isEdit.set({index: externalInfo.positionIndex!});
        this.form.get('editValue')?.reset()
        this.addNew(externalInfo.type, template)

        setTimeout(() => {
            if (this.editType == this.externalInfoType.FROM) {
                this.form.get('editValue')?.get('sensorId')?.setValue(externalInfo.sensorId)
                this.form.get('editValue')?.get('infoName')?.setValue(externalInfo.infoName)
            } else if (this.editType == this.externalInfoType.RECORD_SETPOINT) {
                this.form.get('editValue')?.get('recordSetpoint')?.setValue(externalInfo.recordSetpoint)
            } else if (this.editType == this.externalInfoType.MAX) {
                this.form.get('editValue')?.get('sensorIds')?.setValue(externalInfo?.max?.sensorIds)
                this.form.get('editValue')?.get('infoName')?.setValue(externalInfo?.max?.infoName)
            } else if (this.editType == this.externalInfoType.MEAN) {
                this.form.get('editValue')?.get('sensorIds')?.setValue(externalInfo?.mean?.sensorIds)
                this.form.get('editValue')?.get('infoName')?.setValue(externalInfo?.mean?.infoName)
            } else if (this.editType == this.externalInfoType.MIN) {
                this.form.get('editValue')?.get('sensorIds')?.setValue(externalInfo?.min?.sensorIds)
                this.form.get('editValue')?.get('infoName')?.setValue(externalInfo?.min?.infoName)
            } else if (this.editType == this.externalInfoType.SUM) {
                this.form.get('editValue')?.get('sensorIds')?.setValue(externalInfo?.min?.sensorIds)
                this.form.get('editValue')?.get('infoName')?.setValue(externalInfo?.min?.infoName)
            } else if (this.editType == this.externalInfoType.MOVING_MEAN) {
                this.form.get('editValue')?.get('sensorId')?.setValue(externalInfo?.movingMean?.sensorId)
                this.form.get('editValue')?.get('infoName')?.setValue(externalInfo?.movingMean?.infoName)
                this.form.get('editValue')?.get('minutes')?.setValue(externalInfo?.movingMean?.minutes)
            } else if (this.editType == this.externalInfoType.VARIATION) {
                this.form.get('editValue')?.get('sensorId')?.setValue(externalInfo?.variation?.sensorId)
                this.form.get('editValue')?.get('infoName')?.setValue(externalInfo?.variation?.infoName)
            } else if (this.editType == this.externalInfoType.TIME_DERIVATE) {
                this.form.get('editValue')?.get('sensorId')?.setValue(externalInfo?.timeDerivate?.sensorId)
                this.form.get('editValue')?.get('infoName')?.setValue(externalInfo?.timeDerivate?.infoName)
            } else if (this.editType == this.externalInfoType.DIFF) {
                this.form.get('editValue')?.get('sensorIdsSum')?.setValue(externalInfo.diff?.sensorIdsSum)
                this.form.get('editValue')?.get('sensorIdsDiff')?.setValue(externalInfo.diff?.sensorIdsDiff)
                this.form.get('editValue')?.get('infoName')?.setValue(externalInfo.diff?.infoName)
            }
        })
    }

    editValue() {
        const values = arrayIsSet(this.form.get('value')?.value) ? [...this.form.get('value')?.value] : [];
        if (this.isEdit && this.isEdit()!.index != null && values[this.isEdit()!.index]) {
            values.splice(this.isEdit()!.index, 1)
        }
        values.push(this.getExternalInfoByEditValue());
        this.form.get('value')?.setValue(values)
    }

    deleteValue() {
        const values = arrayIsSet(this.form.get('value')?.value) ? [...this.form.get('value')?.value] : [];
        if (this.isEdit && this.isEdit()!.index != null && values[this.isEdit()!.index]) {
            values.splice(this.isEdit()!.index, 1)
        }
        this.form.get('value')?.setValue(values)
    }

}
